import { RequiredFacebookScopes } from "../../../api/social-account-connect/facebook";
import { FACEBOOK_GROUPS_REMOVED_MSG } from "../../../constants/TooltipMessages";
import { InstagramScopeContentPublish } from "../../../api/social-account-connect/instagram/scopes";
import { INSTAGRAM_CAMPAIGN_ROUTE } from "../../../features/instagram-campaign/campaign-registers";
import { AccountType } from "../../../shared/types/accounts";
import { CurrentUserState } from "../../current-user";

import {
  getAccountPlatformType,
  isAccountFacebookPage,
  isInstagramAccount,
} from "./account-platform";
import { getTwitterLongformCapability } from "./is-twitter-longform-capable";
import { ONLY_MANUAL_PLATFORMS } from "../../../constants/Platforms";
import {
  isAdsFeatureOn,
  isAdsPermissionRequired,
} from "../../../libs/account-permission-check";
import { PlatformNameToPlatformTypeMap } from "../../../features/post/format-post";

export interface AccountStatusType {
  /**
   * The chip's label as well as its discriminator. There is deliberately no
   * separate `label` field: every existing chip renders this string verbatim,
   * and adding one would let the two drift.
   */
  type:
    | "Paused"
    | "Posting"
    | "Insights"
    | "Queue"
    | "Sked Link"
    | "Inbox"
    | "Ads"
    | "X Premium"
    // SKE-4685. The two Instagram publishing states.
    | "Publishing: API"
    | "Publishing: needs update";
  icon: "on" | "off" | "pause" | "questionmark";
  tooltip?: string;
  /**
   * SKE-4685. An in-app route the chip links to.
   *
   * Optional and honoured by the renderer only when present, so the chips that
   * existed before this field are rendered by exactly the same path they were.
   */
  href?: string;
}

export interface AccountStatusOptions {
  /** SKE-4733. The server-resolved `instagramApiPublishing` toggle. */
  instagramApiPublishing?: boolean;
  /** SKE-4733. The server-resolved `instagramReconnectCampaign` toggle. */
  instagramReconnectCampaign?: boolean;
}

/**
 * SKE-4685 — the publishing chip's two tooltips.
 *
 * Exported so the stories and the tests read the shipped strings rather than
 * their own copies of them. The campaign's banned vocabulary applies: no
 * "reconnect", no "migration", no "scope", no "permission set", no "token" and
 * no "composer".
 */
export const INSTAGRAM_PUBLISHING_CHIP_COPY = {
  apiTooltip:
    "This account publishes to Instagram through Meta's publishing API.",
  needsUpdateTooltip:
    "This account still needs updating. Open Update Instagram publishing to finish it.",
} as const;

export const TIKTOK_INBOX_SCOPES = [
  "user.account.type",
  "message.list.read",
  "message.list.send",
  "message.list.manage",
] as const;

type AccountStatusGetter = (
  account: AccountType,
  user?: CurrentUserState | null,
  options?: AccountStatusOptions
) => AccountStatusType[];

export function getAccountStatuses(
  account: AccountType,
  user?: CurrentUserState | null,
  options: AccountStatusOptions = {}
): AccountStatusType[] {
  const platformType = getAccountPlatformType(account);
  let getter =
    GetAccountStatuses[platformType as keyof typeof GetAccountStatuses];
  // non FB or IG accounts
  if (!getter) {
    getter = GetAccountStatuses["__other__"];
  }

  const isTikTokAccount = platformType === PlatformNameToPlatformTypeMap.tiktok;
  const isManualTT = isTikTokAccount && !account.platformId;
  const isThreadsAccount =
    platformType === PlatformNameToPlatformTypeMap.threads;
  const isManualTH = isThreadsAccount && !account.platformId;

  // override for "notification" account types
  if (
    ONLY_MANUAL_PLATFORMS.includes(platformType) ||
    isManualTT ||
    isManualTH
  ) {
    getter = GetAccountStatuses["__notification__"];
  }
  return getter(account, user, options);
}

export function isAccountPostingAndInsightsEnabled(
  account: AccountType,
  options: AccountStatusOptions = {}
): boolean {
  const platformType = getAccountPlatformType(account);
  if (isAccountFacebookPage(account)) {
    return getIsFbPostingOn(account) && getFBInsightsStatusOn(account);
  } else if (platformType === "IG") {
    return (
      getIGPostingStatusOn(account, options) && getIGInsightsStatusOn(account)
    );
  } else {
    return account.status === "ok";
  }
}

/** TODO: duplicated code with public/components/app.js file  */
export function isLinkedInNewPermissionRequired(acc: AccountType) {
  if (!acc) {
    return false;
  }

  const requiredLiScopes = [
    "w_organization_social_feed",
    "r_organization_social_feed",
  ];
  const isLinkedInCompanyProfile = (acc.linkedinUrn || "").includes(
    "organization"
  );
  if (
    acc.platformType !== "LI" ||
    !isLinkedInCompanyProfile ||
    isPausedAccount(acc)
  ) {
    return false;
  }

  const scopes = acc.session?.scopes || [];
  return requiredLiScopes.some((scope) => !scopes.includes(scope));
}

export function isTiktokNewPermissionRequired(acc: AccountType) {
  if (!acc) {
    return false;
  }

  if (
    acc.platformType !== "TT" ||
    !acc.platformId || // old TT notification channel has no platformId
    isPausedAccount(acc)
  ) {
    return false;
  }

  const requiredTTScopes = ["user.info.stats"];

  // @ts-ignore
  const scopes = acc.session?.scope || "";
  return !requiredTTScopes.every((scope) => scopes.includes(scope));
}

/**
 * SKE-5376. Pass the gate from a publishing surface: an Instagram account on
 * the API track is then judged on its API connection alone, and a missing
 * inbox permission is the inbox's question, not a reconnect prompt here.
 */
export function doesAccountNeedApiReconnect(
  account: AccountType,
  options: Required<Pick<AccountStatusOptions, "instagramApiPublishing">>
): boolean {
  const platformType = getAccountPlatformType(account);
  if (isAccountFacebookPage(account)) {
    return !(
      getIsFbPostingOn(account) &&
      getFBInsightsStatusOn(account) &&
      getFBInboxStatusOn(account)
    );
  } else if (platformType === "IG") {
    if (isInstagramPhoneStateIgnored(account, options.instagramApiPublishing)) {
      return !getIGInsightsStatusOn(account);
    }
    return !(getIGInsightsStatusOn(account) && getIGInboxStatusOn(account));
  } else {
    return false;
  }
}

const getIsFbPostingOn = (account: AccountType) => {
  return (
    account.status === "ok" &&
    RequiredFacebookScopes.posting.every((scope) => {
      return account.session?.scopes?.includes(scope);
    })
  );
};
const getFBInsightsStatusOn = (account: AccountType) => {
  return (
    account.status === "ok" &&
    RequiredFacebookScopes.insights.every((scope) => {
      return account.session?.scopes?.includes(scope);
    })
  );
};
const getFBInboxCommentsStatusOn = (account: AccountType) => {
  return (
    account.status === "ok" &&
    ["pages_manage_engagement"].every((scope) => {
      return account.session?.scopes?.includes(scope);
    })
  );
};

const getFBInboxStatusOn = (account: AccountType) => {
  return (
    account.status === "ok" &&
    RequiredFacebookScopes.inbox.every((scope) => {
      return account.session?.scopes?.includes(scope);
    })
  );
};

/**
 * With the API publishing gate on, the phone login controls belong only to an
 * account that already holds a phone login. With it off they show as before.
 *
 * SKE-5376. An account on the API track never shows them in the main account
 * view: they live in its collapsed Phone publishing section instead, however
 * stale the saved phone login is.
 */
export function showsInstagramPhoneLoginControls(
  account: AccountType,
  apiPublishingOn: boolean
): boolean {
  if (!apiPublishingOn) return true;
  return (
    account.hasPhoneLogin === true &&
    !getIGApiPublishingStatusOn(account, apiPublishingOn)
  );
}

/**
 * SKE-5376. THE rule for whether an Instagram account's PHONE login state
 * counts against it. On the API track (the same rule as the "Publishing: API"
 * chip) it does not: the phone `status`, its fail reasons, `insightsOnly`,
 * `legit` and `hasPhoneLogin` say nothing about whether its posts publish.
 * A pause still counts, as do the API's own problems (`promptApiReconnect`,
 * a lost token, `instagramPublishReconnectRequired`, which takes the account
 * off the track). Off the API track, nothing changes.
 */
export function isInstagramPhoneStateIgnored(
  account: AccountType,
  apiPublishingOn: boolean
): boolean {
  return (
    getIGApiPublishingStatusOn(account, apiPublishingOn) &&
    !isPausedAccount(account)
  );
}

const getIGPostingStatusOn = (
  account: AccountType,
  options: AccountStatusOptions
) => {
  const apiPublishingOn = options.instagramApiPublishing === true;
  if (isPausedAccount(account)) return false;
  return (
    getIGApiPublishingStatusOn(account, apiPublishingOn) ||
    (showsInstagramPhoneLoginControls(account, apiPublishingOn) &&
      account.status === "ok")
  );
};

/**
 * A failed status counts against an account unless it is an Instagram account
 * posting through the API with the gate on. A pause always counts.
 */
export function isAccountFailedForPosting(
  account: AccountType,
  options: AccountStatusOptions = {}
): boolean {
  if (isPausedAccount(account)) return true;
  return (
    account?.status === "failed" &&
    !getIGApiPublishingStatusOn(
      account,
      options.instagramApiPublishing === true
    )
  );
}

const getIGInsightsStatusOn = (account: AccountType) => {
  return !!(account.igTokenAddedAt && !account.promptApiReconnect);
};

const getIGInboxStatusOn = (account: AccountType) => {
  // showing inbox enabled IG account only needs "instagram_manage_messages"
  return ["instagram_manage_messages"].every((scope) => {
    return account.session?.scopes?.includes(scope);
  });
};

/**
 * SKE-4527. The client's mirror of the server's per-account Instagram API
 * publishing capability
 * (`apps/schedugram-server/src/app/services/instagram-publishing/capability.ts`).
 *
 * WHAT THIS DELIBERATELY DOES NOT KNOW. The server's answer additionally
 * requires the account to sit on a rollout allow-list and to hold a usable
 * access token. Neither is visible here: the allow-list is an environment
 * value the client never receives, and access tokens are stripped from every
 * account payload sent to the browser. So a `true` from this function means
 * "eligible as far as the client can tell", never "the worker will take the
 * API track". The server is the only authority on that, and it re-derives the
 * full answer at publish time. Treat this as a gate that can only be more
 * permissive than the server's, never less. Anything user-facing built on it
 * must tolerate the server declining afterwards.
 *
 * Scopes are read from both `session` and `longSession` because Facebook Login
 * for Business writes them to whichever the connect path used, exactly as the
 * server's `hasScope` does.
 */
export function getIGApiPublishingStatusOn(
  account: AccountType,
  /**
   * SKE-4733. The server-resolved Instagram API publishing gate for this
   * workspace (`useInstagramApiPublishingOn`). Required, so every caller has to
   * say where it came from. False means the account publishes from the phone,
   * whatever it holds.
   */
  apiPublishingOn: boolean
): boolean {
  if (!apiPublishingOn) {
    return false;
  }
  if (!isInstagramAccount(account)) {
    return false;
  }
  // A person rejected this account; the server refuses it the API track too.
  if (account.status === "notApproved") {
    return false;
  }
  if (!account.instagram_business_account?.id) {
    return false;
  }
  if (!hasIGScope(account, InstagramScopeContentPublish)) {
    return false;
  }
  return account.instagramPublishReconnectRequired !== true;
}

const hasIGScope = (account: AccountType, scope: string) => {
  const sessionScopes = account.session?.scopes ?? [];
  const longSessionScopes = account.longSession?.scopes ?? [];
  return sessionScopes.includes(scope) || longSessionScopes.includes(scope);
};

const getIGInboxCommentsStatusOn = (account: AccountType) => {
  // showing inbox enabled IG account only needs "instagram_manage_messages"
  return ["instagram_manage_comments"].every((scope) => {
    return account.session?.scopes?.includes(scope);
  });
};

const GetAccountStatuses: Record<string, AccountStatusGetter> = {
  FB: checkPausedEnhancer(
    (
      account: AccountType,
      user?: CurrentUserState | null
    ): AccountStatusType[] => {
      const isFbPostingOn = getIsFbPostingOn(account);
      const isFbInsightsOn = getFBInsightsStatusOn(account);
      const isFBInboxOn = getFBInboxStatusOn(account);
      const isFacebookPage = isAccountFacebookPage(account);
      const statuses: AccountStatusType[] = [
        // SKE-4694. A Group can no longer publish, whatever scopes it holds.
        isFacebookPage
          ? { type: "Posting", icon: isFbPostingOn ? "on" : "off" }
          : {
              type: "Posting",
              icon: "off",
              tooltip: FACEBOOK_GROUPS_REMOVED_MSG,
            },
      ];
      if (isFacebookPage) {
        statuses.push({
          type: "Insights",
          icon: isFbInsightsOn ? "on" : "off",
        });
      }
      statuses.push({
        type: "Queue",
        icon: account.queueEnabled ? "on" : "questionmark",
        tooltip: account.queueEnabled
          ? "The queue is set up and active for this account. (Optional)"
          : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
      });

      const showInboxComments = true;

      if (isFacebookPage && showInboxComments) {
        const isFBInboxCommentsOn = getFBInboxCommentsStatusOn(account);

        statuses.push({
          type: "Inbox",
          icon: isFBInboxCommentsOn ? "on" : "questionmark",
          tooltip: isFBInboxCommentsOn
            ? "Sked Inbox is setup for this account"
            : "Please click the Reconnect Inbox/Account button to update permissions for Inbox Comments",
        });
      } else if (isFacebookPage) {
        statuses.push({
          type: "Inbox",
          icon: isFBInboxOn ? "on" : "off",
          tooltip: isFBInboxOn
            ? "Sked Inbox is setup for this account"
            : "Please click the Reconnect Inbox/Account button to update permissions for Inbox",
        });
      }

      if (isAdsFeatureOn(account, user)) {
        const isAdsPermissionExisted = !isAdsPermissionRequired(account, user);
        statuses.push({
          type: "Ads",
          icon: isAdsPermissionExisted ? "on" : "off",
          tooltip: isAdsPermissionExisted
            ? "Meta Ads is setup for this account"
            : "Account is disconnected from Meta Ads. Reconnect to update your permissions.",
        });
      }

      return statuses;
    }
  ),
  IG: checkPausedEnhancer(
    (
      account: AccountType,
      _user?: CurrentUserState | null,
      options: AccountStatusOptions = {}
    ): AccountStatusType[] => {
      const isIGInboxOn = getIGInboxStatusOn(account);

      const statuses: AccountStatusType[] = [
        {
          type: "Posting",
          icon: getIGPostingStatusOn(account, options) ? "on" : "off",
        },
        {
          type: "Insights",
          icon: getIGInsightsStatusOn(account) ? "on" : "off",
        },
        {
          type: "Queue",
          icon: account.queueEnabled ? "on" : "questionmark",
          tooltip: account.queueEnabled
            ? "The queue is set up and active for this account. (Optional)"
            : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
        },
        {
          type: "Sked Link",
          icon:
            account?.igData?.website &&
            account?.igData?.website.includes("sked.link")
              ? "on"
              : "questionmark",
          tooltip:
            account?.igData?.website &&
            account?.igData?.website.includes("sked.link")
              ? "Sked Link is set up for this account. (Optional)"
              : "You have not set up Sked Link for this account yet. Head to 'link in bio' in the menu! (Optional)",
        },
      ];

      const showInboxComments = true;

      if (showInboxComments) {
        const isIGInboxCommentsOn = getIGInboxCommentsStatusOn(account);

        statuses.push({
          type: "Inbox",
          icon: isIGInboxCommentsOn ? "on" : "questionmark",
          tooltip: isIGInboxCommentsOn
            ? "Sked Inbox is set up for this account"
            : "Please use Update Facebook permissions to turn on Inbox Comments",
        });
      } else {
        statuses.push({
          type: "Inbox",
          icon: isIGInboxOn ? "on" : "off",
          tooltip: isIGInboxOn
            ? "Sked Inbox is set up for this account"
            : "Please use Update Facebook permissions to turn on Inbox",
        });
      }

      /*
       * SKE-4685 — the publishing chip.
       *
       * Rendered whatever the campaign is doing, because it describes the
       * ACCOUNT rather than the campaign: an account either publishes through
       * Meta's API or it does not, on a dormant week as much as a live one. Only
       * the banner, the sidebar entry and the calendar marker come and go with
       * the campaign gate.
       *
       * `getIGApiPublishingStatusOn` is the one client-side derivation of this
       * question and the composer already reads it. It is deliberately more
       * permissive than the server's answer; see its doc comment. That is
       * acceptable here because the chip's outstanding state only ever sends a
       * customer to a page that re-asks the server.
       */
      /*
       * SKE-4733. Shown only where it is true for this workspace: "Publishing:
       * API" when the account is on the gated API track, "needs update" only
       * while the reconnect campaign is on. Otherwise no chip at all, which is
       * what every account showed before the campaign existed.
       *
       * SKE-5001. "needs update" asks whether the ACCOUNT lacks something, so
       * it passes `true` for the gate rather than the workspace's value. The
       * gate being off must not make a fully authorised account read "needs
       * update" here while the campaign checklist, which ignores the gate on
       * purpose (`CAMPAIGN_AUTHORISATION_BLOCKERS` on the server), calls the
       * same account updated. Authorised with the gate off gets no chip. A
       * rejected (`notApproved`) account gets none either: authorising cannot
       * fix a rejection, and the checklist leaves it out of its blockers too.
       * A demo account may show "Publishing: API", never "needs update" (SKE-5369).
       */
      if (
        getIGApiPublishingStatusOn(
          account,
          options.instagramApiPublishing === true
        )
      ) {
        statuses.push({
          type: "Publishing: API",
          icon: "on",
          tooltip: INSTAGRAM_PUBLISHING_CHIP_COPY.apiTooltip,
        });
      } else if (
        options.instagramReconnectCampaign === true &&
        account.status !== "notApproved" &&
        account.isMarketingDemo !== true &&
        !getIGApiPublishingStatusOn(account, true)
      ) {
        statuses.push({
          type: "Publishing: needs update",
          icon: "off",
          tooltip: INSTAGRAM_PUBLISHING_CHIP_COPY.needsUpdateTooltip,
          href: INSTAGRAM_CAMPAIGN_ROUTE,
        });
      }

      return statuses;
    }
  ),
  TH: checkPausedEnhancer((account: AccountType): AccountStatusType[] => {
    return [
      {
        type: "Posting",
        icon: account.status === "ok" ? "on" : "off",
        //tooltip: "Posting will be available soon for Threads accounts.",
      },
      {
        type: "Insights",
        //icon: !isThreadsNewPermissionRequired(account) ? "on" : "off",
        icon: "off",
        tooltip: "Insights are not available for Threads accounts.",
      },
      {
        type: "Queue",
        icon: account.queueEnabled ? "on" : "questionmark",
        tooltip: account.queueEnabled
          ? "The queue is set up and active for this account. (Optional)"
          : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
      },
    ];
  }),
  TT: checkPausedEnhancer((account: AccountType): AccountStatusType[] => {
    const statuses: AccountStatusType[] = [
      {
        type: "Posting",
        icon: account.status === "ok" ? "on" : "off",
      },
      {
        type: "Insights",
        icon: !isTiktokNewPermissionRequired(account) ? "on" : "off",
      },
      {
        type: "Queue",
        icon: account.queueEnabled ? "on" : "questionmark",
        tooltip: account.queueEnabled
          ? "The queue is set up and active for this account. (Optional)"
          : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
      },
    ];

    const grantedScopes = new Set(
      (account.session?.scope || "").split(/[\s,]+/).filter(Boolean)
    );
    const hasInboxPermissions = TIKTOK_INBOX_SCOPES.every((scope) =>
      grantedScopes.has(scope)
    );
    const isBusinessAccount = account.ttData?.is_business_account;

    if (!hasInboxPermissions || typeof isBusinessAccount !== "boolean") {
      statuses.push({
        type: "Inbox",
        icon: "questionmark",
        tooltip:
          "Reconnect this account to grant the permissions required for Inbox.",
      });
    } else if (!isBusinessAccount) {
      statuses.push({
        type: "Inbox",
        icon: "questionmark",
        tooltip:
          "Comments only. Direct messages are limited to TikTok Business Accounts.",
      });
    } else {
      statuses.push({
        type: "Inbox",
        icon: "on",
        tooltip: "Sked Inbox is set up for this account.",
      });
    }

    return statuses;
  }),
  TW: checkPausedEnhancer((account: AccountType): AccountStatusType[] => {
    const statuses: AccountStatusType[] = [
      {
        type: "Posting",
        icon: account.status === "ok" ? "on" : "off",
      },
      {
        type: "Queue",
        icon: account.queueEnabled ? "on" : "questionmark",
        tooltip: account.queueEnabled
          ? "The queue is set up and active for this account. (Optional)"
          : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
      },
    ];
    // X Premium capability chip — capable shows "X Premium" on, confirmed
    // non-premium shows "X Premium" off. Unknown (never refreshed) shows nothing.
    const capability = getTwitterLongformCapability(account);
    if (capability === "capable") {
      statuses.push({
        type: "X Premium",
        icon: "on",
        tooltip:
          "This account is on X Premium, so long-form posting is available.",
      });
    } else if (capability === "not-capable") {
      statuses.push({
        type: "X Premium",
        icon: "off",
        tooltip:
          "This account is not on X Premium, so long-form posting is unavailable.",
      });
    }
    return statuses;
  }),
  // Notification accounts don't publish automatically so only show queue option
  __notification__: checkPausedEnhancer(
    (account: AccountType): AccountStatusType[] => {
      return [
        {
          type: "Queue",
          icon: account.queueEnabled ? "on" : "questionmark",
          tooltip: account.queueEnabled
            ? "The queue is set up and active for this account. (Optional)"
            : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
        },
      ];
    }
  ),
  __other__: checkPausedEnhancer(
    (account: AccountType): AccountStatusType[] => {
      return [
        {
          type: "Posting",
          icon: account.status === "ok" ? "on" : "off",
        },
        {
          type: "Queue",
          icon: account.queueEnabled ? "on" : "questionmark",
          tooltip: account.queueEnabled
            ? "The queue is set up and active for this account. (Optional)"
            : "You have not set up the queue for this account yet. Check out 'queue' on the right! (Optional)",
        },
      ];
    }
  ),
};

function checkPausedEnhancer(
  getter: (
    account: AccountType,
    user?: CurrentUserState | null,
    options?: AccountStatusOptions
  ) => AccountStatusType[]
): (
  account: AccountType,
  user?: CurrentUserState | null,
  options?: AccountStatusOptions
) => AccountStatusType[] {
  return (
    account: AccountType,
    user?: CurrentUserState | null,
    options?: AccountStatusOptions
  ): AccountStatusType[] => {
    if (isPausedAccount(account)) {
      return [{ type: "Paused", icon: "pause" }];
    } else {
      return getter(account, user, options);
    }
  };
}

export function isPausedAccount(account: AccountType): boolean {
  return (
    account?.status === "failed" &&
    Boolean(account?.failReason?.match(/you set this account to be paused/i))
  );
}
